import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ApplicationService from 'src/services/ApplicationService';
import {
  PlaybackActionTypes,
  SELECTOR_PLAYBACK_CURRENT_TIME,
  SELECTOR_PLAYBACK_IS_CLOSE_TO_LIVE_EDGE,
  SELECTOR_PLAYBACK_BUFFER_INDICATOR_PERCENT,
  SELECTOR_PLAYBACK_TIME_PERCENT,
  SELECTOR_PLAYBACK_DURATION,
} from 'src/store/reducers/playback';

import { EVENT_TYPES, TRANSMISSION_TYPE } from 'src/interfaces/enums';
import LiveService from 'src/services/LiveService';
import useSeek from '../useSeek';
import useApplicationData from '../useApplicationData';

const useWatchTime = () => {
  const timePercent = useSelector(SELECTOR_PLAYBACK_TIME_PERCENT);
  const bufferIndicatorPercent = useSelector(SELECTOR_PLAYBACK_BUFFER_INDICATOR_PERCENT);
  const currentTime = useSelector(SELECTOR_PLAYBACK_CURRENT_TIME);
  const duration = useSelector(SELECTOR_PLAYBACK_DURATION);
  const isCloseToLiveEdge = useSelector(SELECTOR_PLAYBACK_IS_CLOSE_TO_LIVE_EDGE);

  const dispatch = useDispatch();

  const { onSeekInit, onSeekEnded } = useSeek();
  const { transmissionType } = useApplicationData();

  const isLive = transmissionType === TRANSMISSION_TYPE.live;

  const updateDuration = useCallback(() => {
    dispatch({
      type: PlaybackActionTypes.UPDATE_DURATION,
      payload: ApplicationService.player?.duration,
    });
  }, [dispatch]);

  const updateCurrentTime = useCallback(() => {
    updateDuration();
    dispatch({
      type: PlaybackActionTypes.UPDATE_CURRENT_TIME,
      payload: ApplicationService.player?.currentTime,
    });
  }, [dispatch, updateDuration]);

  const setIsCloseToLiveEdge = useCallback(() => {
    dispatch({
      type: PlaybackActionTypes.SET_IS_CLOSE_TO_LIVE_EDGE,
      payload: (ApplicationService.player as LiveService)?.isCloseToLiveEdge(),
    });
  }, [dispatch]);

  const updateWatchTime = useCallback(() => {
    updateCurrentTime();
    isLive && setIsCloseToLiveEdge();
    dispatch({
      type: PlaybackActionTypes.UPDATE_TIME_PERCENT,
      payload: ApplicationService.player?.percentWatched,
    });
  }, [dispatch, isLive, setIsCloseToLiveEdge, updateCurrentTime]);

  const setWatchTime = useCallback(
    (timePercent: number) => {
      ApplicationService.player?.setCurrentTimePercent(timePercent);

      dispatch({
        type: PlaybackActionTypes.UPDATE_TIME_PERCENT,
        payload: timePercent,
      });
    },
    [dispatch],
  );

  const forwardSeconds = useCallback(
    (seconds: number) => {
      onSeekInit();
      ApplicationService.player?.setCurrentTime(seconds + ApplicationService.player?.currentTime);
      onSeekEnded();
    },
    [onSeekEnded, onSeekInit],
  );

  const backwardSeconds = useCallback(
    (seconds: number) => {
      onSeekInit();
      ApplicationService.player?.setCurrentTime(ApplicationService.player?.currentTime - seconds);
      onSeekEnded();
    },
    [onSeekEnded, onSeekInit],
  );

  const updateBufferIndicator = useCallback(() => {
    dispatch({
      type: PlaybackActionTypes.UPDATE_BUFFER_INDICATOR_PERCENT,
      payload: ApplicationService.player?.bufferIndicatorPercent,
    });
  }, [dispatch]);

  const setPlayerTime = useCallback((time: number) => {
    if (!ApplicationService.player?.hasStarted()) ApplicationService.player?.disableLoader();
    if (ApplicationService.player?.readyState() === 0) {
      ApplicationService.player?.addFirstEventListener(EVENT_TYPES.LOADED_METADATA, () =>
        ApplicationService.player?.setFirstPlayTime(time),
      );
    } else {
      ApplicationService.player?.setCurrentTime(time);
    }
  }, []);

  const seekToLiveEdge = useCallback(() => {
    (ApplicationService.player as LiveService)?.liveTracker?.seekToLiveEdge();
  }, []);

  return {
    bufferIndicatorPercent,
    timePercent,
    currentTime,
    duration,
    updateWatchTime,
    updateBufferIndicator,
    forwardSeconds,
    setWatchTime,
    setPlayerTime,
    backwardSeconds,
    isCloseToLiveEdge,
    seekToLiveEdge,
    setIsCloseToLiveEdge,
  };
};

export default useWatchTime;
